import {
  EnvironmentInjector,
  Injectable,
  Injector,
  ApplicationRef,
  createComponent,
  EmbeddedViewRef,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { take } from 'rxjs';
import { CommonService } from './common.service';
// imports you will need in the file (add these to top of the TS file)
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { CommercialInvoiceComponent } from 'src/app/shared/components/commercial-invoice/commercial-invoice.component';
import { AirWaybillGenerationPreviewComponent } from '../components/air-waybill-generation-preview/air-waybill-generation-preview.component';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private openedPopcount = 0;

  constructor(
    private readonly dialog: MatDialog,
    private readonly commonService: CommonService,
    private readonly injector: Injector,
    private readonly appRef: ApplicationRef,
    private readonly environmentInjector: EnvironmentInjector
  ) {}

  public openPopup(
    code: any,
    title: any,
    component: any,
    width: string,
    injectedComponent?: any
  ) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = width; // Dynamically set the width
    dialogConfig.data = {
      title: title,
      code: code,
      injectedComponent: injectedComponent,
    };

    // does not let Modal auto-close on backdrop click
    dialogConfig.disableClose = true;
    const dialogRef = this.dialog.open(component, dialogConfig);
    dialogRef
      .afterOpened()
      .pipe(take(1))
      .subscribe(() => {
        this.commonService.setOverlayOpen(true);
        this.openedPopcount++;
      });
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.openedPopcount--;
        if (this.openedPopcount === 0) {
          this.commonService.setOverlayOpen(false);
        }
      });
    return dialogRef;
  }

  public async downloadCommercialInvoiceDirect(
    data: any,
    isForAirWaybills?: boolean
  ): Promise<void> {
    const abortController = new AbortController();
    let compRef: any = undefined;
    let container: HTMLElement | null = null;
    let filename: string = '';
    let revokeUrlTimer: ReturnType<typeof setTimeout> | undefined;
    const formateDate = (date: any) => {
      return date.replaceAll(/[:\s]/g, '_');
    };
    try {
      if (isForAirWaybills) {
        compRef = createComponent(CommercialInvoiceComponent, {
          environmentInjector: this.environmentInjector,
        });
        compRef.instance.airwayBillInvoiceData = data;
        filename = 'Commercial_Invoice_' + formateDate(data?.date) + '.pdf';
      } else {
        compRef = createComponent(AirWaybillGenerationPreviewComponent, {
          environmentInjector: this.environmentInjector,
        });
        compRef.instance.airwayBillData = data;
        filename =
          'Air_Waybill_' + formateDate(data?.createdDateAndTime) + '.pdf';
      }

      this.appRef.attachView(compRef.hostView);
      compRef.changeDetectorRef.detectChanges();

      const domElem = (compRef.hostView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement;

      container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '-10000px';
      container.style.top = '0px';
      container.style.width = '1200px';
      container.style.overflow = 'hidden';
      container.style.paddingTop = '40px';
      container.style.paddingRight = '40px';
      container.style.paddingBottom = '40px';
      container.style.paddingLeft = '40px';
      container.appendChild(domElem);
      document.body.appendChild(container);
      await this.waitForRenderAndImages(domElem, 500, abortController.signal);
      if (abortController.signal.aborted) {
        return;
      }
      if (!isForAirWaybills) {
        await this.delay(250, abortController.signal);
      }
      if (abortController.signal.aborted) {
        return;
      }
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
      });

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const imgData = canvas.toDataURL('image/png');
      canvas.width = 0;
      canvas.height = 0;

      const pdf = new jsPDF({
        unit: 'px',
        format: [canvasWidth, canvasHeight],
        orientation: canvasWidth >= canvasHeight ? 'landscape' : 'portrait',
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvasWidth, canvasHeight);

      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      revokeUrlTimer = setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        console.error('Direct invoice download failed', err);
      }
    } finally {
      abortController.abort();
      if (revokeUrlTimer) {
        clearTimeout(revokeUrlTimer);
      }
      try {
        if (compRef?.hostView) {
          try {
            this.appRef.detachView(compRef.hostView);
          } catch (detachErr) {
            console.warn(
              'Failed to detach view from ApplicationRef',
              detachErr
            );
          }
          try {
            compRef.destroy();
          } catch (destroyErr) {
            console.warn('Failed to destroy component reference', destroyErr);
          }
        }
        container?.parentNode?.removeChild(container);
      } catch (cleanupErr) {
        console.warn('Cleanup after direct download failed', cleanupErr);
      }
    }
  }

  private delay(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      const timer = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      const onAbort = () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      signal?.addEventListener('abort', onAbort, { once: true });
    });
  }

  public waitForRenderAndImages(
    rootEl: HTMLElement,
    extraDelay = 300,
    signal?: AbortSignal
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const pendingTimers = new Set<ReturnType<typeof setTimeout>>();
      let settled = false;

      const schedule = (callback: () => void, delay: number) => {
        const timer = setTimeout(() => {
          pendingTimers.delete(timer);
          callback();
        }, delay);
        pendingTimers.add(timer);
      };

      const clearPendingTimers = () => {
        pendingTimers.forEach(timer => clearTimeout(timer));
        pendingTimers.clear();
      };

      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        clearPendingTimers();
        signal?.removeEventListener('abort', onAbort);
        resolve();
      };

      const onAbort = () => {
        if (settled) {
          return;
        }
        settled = true;
        clearPendingTimers();
        signal?.removeEventListener('abort', onAbort);
        reject(new DOMException('Aborted', 'AbortError'));
      };

      if (signal?.aborted) {
        onAbort();
        return;
      }
      signal?.addEventListener('abort', onAbort);

      schedule(() => {
        const imgs = Array.from(
          rootEl.querySelectorAll<HTMLImageElement>('img')
        );

        if (!imgs.length) {
          schedule(finish, extraDelay);
          return;
        }

        let remaining = imgs.length;
        const onImageDone = () => {
          remaining--;
          if (remaining <= 0) {
            schedule(finish, extraDelay);
          }
        };

        imgs.forEach(img => {
          if (img.complete) {
            onImageDone();
          } else {
            img.addEventListener('load', onImageDone, { once: true });
            img.addEventListener('error', onImageDone, { once: true });
          }
        });

        schedule(finish, 3000 + extraDelay);
      }, 50);
    });
  }
}
